import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useNavigate, NavLink } from 'react-router-dom';
import api from './utils/api';
import StockTable from './components/StockTable';
import Dashboard from './components/Dashboard';
import Login from './components/Login';
import Admin from './components/Admin';
import Orders from './components/Orders';
import Analytics from './components/analytics/Analytics';
import BarcodeScanner from './components/BarcodeScanner';
import WarehouseOrders from './components/WarehouseOrders';
import ThreePLHub from './components/3PLHub';
import PurchaseOrders from './components/PurchaseOrders';
import Inputs from './components/Inputs';
import Sidebar, { NavItem } from './components/Sidebar';
import ListsPage from './components/tasks/ListsPage';
import CalendarPage from './components/tasks/CalendarPage';
import ProjectsPage from './components/projects/ProjectsPage';
import ProjectPage from './components/projects/ProjectPage';
import { TaskComposerHost } from './components/tasks/TaskComposer';
import ListDialogHost from './components/tasks/ListDialog';
import ProtectedRoute from './routes/ProtectedRoute';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ToastProvider } from './context/ToastContext';
import { TasksProvider, useTasks } from './context/TasksContext';
import { Warehouse } from './types';
import './core.css';

const MainContent: React.FC = () => {
  const { user, permissions, logout } = useAuth();
  const { notifications, openComposer, ready: tasksReady } = useTasks();
  const navigate = useNavigate();
  const isAdmin = permissions?.user_type === 'Admin';
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [isLoadingWarehouses, setIsLoadingWarehouses] = useState(true);

  // Sidebar state
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [sidebarWidth, setSidebarWidth] = useState(280);
  const [sidebarMobileOpen, setSidebarMobileOpen] = useState(false);

  // Update CSS custom property for dynamic overlay positioning
  useEffect(() => {
    const currentWidth = sidebarCollapsed ? 64 : sidebarWidth;
    document.documentElement.style.setProperty('--sidebar-width', `${currentWidth}px`);
  }, [sidebarCollapsed, sidebarWidth]);

  const fetchWarehouses = async () => {
    setIsLoadingWarehouses(true);
    try {
      const response = await api.get('/neon-stock');
      setWarehouses(response.data.warehouses);
    } catch (error: any) {
      console.error('Fetch warehouses error:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
      });
      if (error.response?.status === 401 || error.response?.status === 403) {
        console.log('Unauthorized, redirecting to /login');
        navigate('/login');
      }
    } finally {
      setIsLoadingWarehouses(false);
    }
  };

  useEffect(() => {
    if (user) {
      fetchWarehouses();
    } else {
      setIsLoadingWarehouses(false);
    }
  }, [user]);

  const editableWarehouses = permissions?.editable_warehouses || [];
  // A Regular user with a warehouse (Admin › Users): sees that warehouse's
  // inputs and can send requests from there.
  const isWarehouseUser = !isAdmin && editableWarehouses.length > 0;
  const barcodeWarehouses = warehouses.filter((warehouse) =>
    (isAdmin || editableWarehouses.includes(warehouse.name)) &&
    (warehouse.barcode_production || warehouse.barcode_shipping)
  );
  const tplWarehouses = warehouses.filter((warehouse) =>
    warehouse["3PL_shipping"]
  );

  // Sidebar: top-level pages, with Stock / Scan Inventory / Warehouses as
  // expandable groups. A group with a single entry collapses into a plain link.
  const generateNavItems = (): NavItem[] => {
    const items: NavItem[] = [];
    const pushGroup = (label: string, iconType: string, children: NavItem[]) => {
      if (children.length === 1) items.push({ ...children[0], label, iconType });
      else if (children.length > 1) items.push({ label, iconType, children });
    };

    items.push({ to: '/', label: 'Dashboard', iconType: 'dashboard' });

    const stock: NavItem[] = [];
    if (permissions?.home_page) stock.push({ to: '/stock', label: 'Products', iconType: 'stock' });
    if (isAdmin || isWarehouseUser) stock.push({ to: '/inputs', label: 'Inputs', iconType: 'inputs' });
    pushGroup('Stock', 'stock', stock);

    if (permissions?.orders_page) {
      items.push({ to: '/orders', label: 'Orders', iconType: 'orders' });
    }

    items.push({ to: '/lists', label: 'Lists', iconType: 'lists', badge: notifications.length || undefined });
    items.push({ to: '/calendar', label: 'Calendar', iconType: 'calendar' });
    items.push({ to: '/projects', label: 'Projects', iconType: 'projects' });

    if (isAdmin) {
      items.push({ to: '/purchase-orders', label: 'Purchase Orders', iconType: 'purchase-orders' });
      items.push({ to: '/analytics', label: 'Analytics', iconType: 'analytics' });
    }

    // All barcode scanning, one entry per warehouse
    const scan: NavItem[] = [];
    barcodeWarehouses.forEach((warehouse) => {
      if (warehouse.barcode_production && permissions?.barcode_production) {
        scan.push({ to: `/warehouse-${warehouse.id}-production`, label: warehouse.name, iconType: 'inventory' });
      }
    });
    pushGroup('Scan Inventory', 'scan', scan);

    // Warehouse order queues and 3PL hubs
    const warehouseLinks: NavItem[] = [];
    barcodeWarehouses.forEach((warehouse) => {
      if (warehouse.barcode_shipping && permissions?.barcode_shipping) {
        // Show MPP orders only for admins for now
        if (warehouse.id === 1 || (warehouse.id === 2 && isAdmin)) {
          warehouseLinks.push({ to: `/warehouse-${warehouse.id}-orders`, label: `${warehouse.name} Orders`, iconType: 'warehouse-orders' });
        }
      }
    });
    if (isAdmin) {
      tplWarehouses.forEach((warehouse) => {
        warehouseLinks.push({ to: `/warehouse-${warehouse.id}-hub`, label: `${warehouse.name} Hub`, iconType: 'hub' });
      });
    }
    pushGroup('Warehouses', 'warehouses', warehouseLinks);

    if (isAdmin) {
      items.push({ to: '/admin', label: 'Admin', iconType: 'admin' });
    }

    return items;
  };

  const generateBarcodeRoutes = () => {
    const routes: React.ReactNode[] = [];
    barcodeWarehouses.forEach((warehouse) => {
      if (warehouse.barcode_production) {
        const route = `warehouse-${warehouse.id}-production`;
        routes.push(
          <Route
            key={route}
            path={`/${route}`}
            element={
              <ProtectedRoute>
                {permissions?.barcode_production ? (
                  <BarcodeScanner
                    warehouseId={warehouse.id.toString()}
                    action="production"
                    title={`${warehouse.name} Production`}
                    instruction={`Scan barcodes to add to ${warehouse.name} stock`}
                  />
                ) : (
                  <div className="container">
                    <h2>Access Denied</h2>
                    <p>You do not have permission to view this page.</p>
                  </div>
                )}
              </ProtectedRoute>
            }
          />
        );
      }
      if (warehouse.barcode_shipping) {
        const route = `warehouse-${warehouse.id}-orders`;
        routes.push(
          <Route
            key={route}
            path={`/${route}`}
            element={
              <ProtectedRoute>
                {permissions?.barcode_shipping ? (
                  <WarehouseOrders isAdmin={isAdmin} warehouseId={warehouse.id} />
                ) : (
                  <div className="container">
                    <h2>Access Denied</h2>
                    <p>You do not have permission to view this page.</p>
                  </div>
                )}
              </ProtectedRoute>
            }
          />
        );
      }
    });
    return routes;
  };

  const generateTplRoutes = () => {
    const routes: React.ReactNode[] = [];
    tplWarehouses.forEach((warehouse) => {
      const route = `warehouse-${warehouse.id}-hub`;
      routes.push(
        <Route
          key={route}
          path={`/${route}`}
          element={
            <ProtectedRoute>
              {isAdmin ? (
                <ThreePLHub warehouseId={warehouse.id} />
              ) : (
                <div className="container">
                  <h2>Access Denied</h2>
                  <p>Only Admins can access 3PL Hub pages.</p>
                </div>
              )}
            </ProtectedRoute>
          }
        />
      );
    });
    return routes;
  };

  return (
    <>
      {user && !isLoadingWarehouses && (
        <Sidebar
          navItems={generateNavItems()}
          onLogout={logout}
          onNewTask={tasksReady ? () => openComposer({}) : undefined}
          isCollapsed={sidebarCollapsed}
          onCollapsedChange={setSidebarCollapsed}
          width={sidebarWidth}
          onWidthChange={setSidebarWidth}
          mobileOpen={sidebarMobileOpen}
          onMobileOpenChange={setSidebarMobileOpen}
        />
      )}

      <div
        className={`main-content ${user && !isLoadingWarehouses ? 'sidebar-present' : ''}`}
        style={{ marginLeft: user && !isLoadingWarehouses ? (sidebarCollapsed ? 64 : sidebarWidth) : 0 }}
      >
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <Dashboard isAdmin={isAdmin} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/stock"
            element={
              <ProtectedRoute>
                {permissions?.home_page ? (
                  <StockTable isAdmin={isAdmin} />
                ) : (
                  <div className="container">
                    <h2>Access Denied</h2>
                    <p>You do not have permission to view this page.</p>
                  </div>
                )}
              </ProtectedRoute>
            }
          />
          <Route
            path="/orders"
            element={
              <ProtectedRoute>
                {permissions?.orders_page ? (
                  <Orders isAdmin={isAdmin} />
                ) : (
                  <div className="container">
                    <h2>Access Denied</h2>
                    <p>You do not have permission to view this page.</p>
                  </div>
                )}
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin"
            element={
              <ProtectedRoute>
                {isAdmin ? <Admin /> : (
                  <div className="container">
                    <h2>Access Denied</h2>
                    <p>Only Admins can access the Admin page.</p>
                  </div>
                )}
              </ProtectedRoute>
            }
          />
          <Route
            path="/analytics"
            element={
              <ProtectedRoute>
                {isAdmin ? <Analytics isAdmin={isAdmin} /> : (
                  <div className="container">
                    <h2>Access Denied</h2>
                    <p>Only Admins can access the Analytics page.</p>
                  </div>
                )}
              </ProtectedRoute>
            }
          />
          <Route
            path="/purchase-orders"
            element={
              <ProtectedRoute>
                {isAdmin ? <PurchaseOrders /> : (
                  <div className="container">
                    <h2>Access Denied</h2>
                    <p>Only Admins can access the Purchase Orders page.</p>
                  </div>
                )}
              </ProtectedRoute>
            }
          />
          <Route
            path="/inputs"
            element={
              <ProtectedRoute>
                {isAdmin || isWarehouseUser ? <Inputs /> : (
                  <div className="container">
                    <h2>Access Denied</h2>
                    <p>The Inputs page is for admins and warehouse users.</p>
                  </div>
                )}
              </ProtectedRoute>
            }
          />
          <Route
            path="/lists"
            element={
              <ProtectedRoute>
                <ListsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/calendar"
            element={
              <ProtectedRoute>
                <CalendarPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/projects"
            element={
              <ProtectedRoute>
                <ProjectsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/projects/:id"
            element={
              <ProtectedRoute>
                <ProjectPage />
              </ProtectedRoute>
            }
          />
          {generateBarcodeRoutes()}
          {generateTplRoutes()}
        </Routes>
      </div>

      {user && <TaskComposerHost />}
      {user && <ListDialogHost />}
    </>
  );
};

// One tasks store per signed-in user; re-keyed so signing in as someone else
// never shows the previous user's lists.
const TasksGate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  return <TasksProvider key={user?.email || 'signed-out'}>{children}</TasksProvider>;
};

const AppContent: React.FC = () => {
  return (
    <Router>
      <TasksGate>
        <MainContent />
      </TasksGate>
    </Router>
  );
};

const App: React.FC = () => {
  return (
    <AuthProvider>
      <ToastProvider>
        <AppContent />
      </ToastProvider>
    </AuthProvider>
  );
};

export default App;